<template>
  <div class="mx-6" v-resize="onResize">
    <div :class="$vuetify.breakpoint.xs ? 'text-h6 mt-4' : 'text-h5'">
      <div class="d-flex mr-5">
        <div class="d-flex">
          <v-icon class="mt-0" :large="!$vuetify.breakpoint.xs">mdi-knife</v-icon>
          <div class="mt-2 pt-3 ml-2">Dissect</div>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                  color="success"
                  class="ml-4 mt-4"
                  fab
                  v-on="on"
                  small
                  @click="downloadCsv()">
                  <v-icon>mdi-download</v-icon>
                </v-btn>
            </template>
            <span>Download to Excel</span>
          </v-tooltip>
        </div>
        <v-spacer /><v-spacer />
        <v-text-field
          class="pt-5 mt-0 mr-4"
          v-model="search"
          clearable
          label="Search"
          type="text"
          prepend-icon="mdi-magnify"
        />
        <v-btn
          class="mt-3"
          color="primary"
          :large="$vuetify.breakpoint.xs ? false : true"
          @click="addItem()"
        >
          <v-icon>mdi-plus</v-icon>
          <div v-show="$vuetify.breakpoint.xs ? false : true">
            Add
          </div>
        </v-btn>
      </div>
    </div>
    <div>
      <v-row class="justify-center">
          <v-col cols="2">
            <v-select v-model="selectedFarms"
              label="Farm"
              chips
              small-chips
              multiple
              clearable
              :items="farmList"
            />
          </v-col>
          <v-col cols="2">
            <v-select v-model="selectedLines"
              label="Line"
              chips
              small-chips
              multiple
              clearable
              :items="lineList"
            />
          </v-col>
          <v-col cols="2">
            <v-select
              label="Sex"
              chips
              small-chips
              multiple
              clearable
              v-model="selectedSexes"
              :items="sexList"
            />
          </v-col>
          <v-col cols="2">
            <v-select
              label="Start Mg_H"
              chips
              small-chips
              multiple
              clearable
              v-model="selectedStartMG_H"
              :items="startMgHList"
            />
          </v-col>
      </v-row>
    </div>
    <v-data-table
      :height="tableHeight"
      class="mt-3 mx-5"
      fixed-header
      multi-sort
      :search="search"
      :headers="tableHeaders"
      :items="filteredItems"
      no-data-text="No Data Available">
        <template v-slot:[`item.isEnabled`]="{ item }">
          <div class="d-flex justify-center">
            <v-icon v-if="item.isEnabled">mdi-checkbox-marked-outline</v-icon>
            <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
          </div>
        </template>
        <template v-slot:[`item.action`]="{ item }">
          <iag-action-buttons
            primary-button-icon="mdi-square-edit-outline"
            @primary-button-clicked="editItem(item)"
            secondary-button-icon="mdi-trash-can-outline"
            @secondary-button-clicked="confirmDeleteItem(item)"/>
        </template>
    </v-data-table>

    <v-dialog v-model="showDeleteDialog" persistent max-width="320">
      <v-card>
        <v-card-title class="text-h5">
          Confirm Delete
        </v-card-title>
        <v-card-text>
            Ok to Delete Id: {{itemToDelete.Id}}?
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer />
          <iag-action-buttons
            primary-button-text="Yes"
            secondary-button-text="No"
            @primary-button-clicked="deleteItem()"
            @secondary-button-clicked="showDeleteDialog = false" />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-form ref="form" v-model="validForm">
      <v-dialog v-model="showChangeDialog" 
          scrollable 
          persistent
          max-width="600px">
        <v-card>
          <v-card-title class="text-h5">
              {{isAdd ? 'Add' : 'Edit'}} Dissect {{isAdd ? '': `(Id: ${editedItem.Id})`}}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="4">
                <v-combobox
                  v-model="editedItem.Farm"
                  :items="farmList"
                  label="Farm"
                  @blur="editedItem.Farm=toAlphaUpperCase(editedItem.Farm)"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="4">
                <v-combobox
                  v-model="editedItem.Line"
                  :items="lineList"
                  label="Line"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="4">
                <v-select
                  v-model="editedItem.Sex"
                  :items="sexList"
                  label="Sex"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="4">
                <v-combobox
                  v-model="editedItem.StartMG_H"
                  :items="startMgHList"
                  label="Start MG_H"
                  :rules="[rules.required]"
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-model="editedItem.Hatch"
                  label="Hatch"
                  :rules="[rules.required, rules.numeric]"
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-model="editedItem.O"
                  label="Old"
                  :rules="[rules.required, rules.numeric]"
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-model="editedItem.M"
                  label="Middle"
                  :rules="[rules.required, rules.numeric]"
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-model="editedItem.Y"
                  label="Young"
                  :rules="[rules.required, rules.numeric]"
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  v-model="editedItem.V"
                  label="Very Old"
                  :rules="[rules.required, rules.numeric]"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.Comment"
                  label="Comment" />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions ref="saveAction" class="pb-3">
            <v-spacer />
            <iag-action-buttons
              :primary-button-disabled="!validForm"
              primary-button-text="Save"
              secondary-button-text="Cancel"
              @primary-button-clicked="saveItem()"
              @secondary-button-clicked="closeChangeDialog()"
            />
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-form>

    <v-overlay :value="isBusy">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>
<script>
import ActionButtons from './../components/ActionButtons.vue'
import iagApi from '../api/iagApi'
import json2csv from "../utils/json2csv"

export default {
    components: {
    'iag-action-buttons': ActionButtons,
  },
  data() {
    return {
      items: [],
      editedItem: {},
      isBusy: true,
      isAdd: false,
      itemToDelete: {},
      rules: null,
      search: '',
      selectedFarms: [],
      selectedLines: [],
      selectedSexes: [],
      selectedStartMG_H: [],
      selectedHatch: [],
      selectedO: [],
      selectedM: [],
      selectedY: [],
      selectedV: [],
      showChangeDialog: false,
      showDeleteDialog: false,
      windowSize: {x: 0,y: 0},
      validForm: true
    }
  },
  mounted () {
    this.onResize();
  },
  async created() {
    try {
      this.isBusy = true;
      this.rules = { ...this.$root.validationRules };
      await this.getDissectData();
    }
    finally {
      this.isBusy = false;
    }
  },
  computed: {
    filteredItems() {
      return this.items.filter(item =>
        {
          let showItem = 
            (this.selectedFarms.length == 0 || this.selectedFarms.includes(item.Farm)) &&
            (this.selectedLines.length == 0 || this.selectedLines.includes(item.Line)) &&
            (this.selectedSexes.length == 0 || this.selectedSexes.includes(item.Sex)) &&
            (this.selectedStartMG_H.length == 0 || this.selectedStartMG_H.includes(item.StartMG_H)) &&
            (this.selectedHatch.length == 0 || this.selectedHatch.includes(item.Hatch)) &&
            (this.selectedO.length == 0 || this.selectedHatch.includes(item.selectedO)) &&
            (this.selectedM.length == 0 || this.selectedHatch.includes(item.selectedM)) &&
            (this.selectedY.length == 0 || this.selectedHatch.includes(item.selectedY)) &&
            (this.selectedV.length == 0 || this.selectedHatch.includes(item.selectedV))
          ;
          return showItem;
        }
      );
    },
    tableHeaders() {
      return [
        { text: 'Id', value: 'Id', sortable: true, align: 'center' },
        { text: 'Farm', value: 'Farm', sortable: true, align: 'center' },
        { text: 'Line', value: 'Line', sortable: true, align: 'center' },
        { text: 'Sex', value: 'Sex', sortable: true, align: 'center' },
        { text: 'Start MG_H', value: 'StartMG_H', sortable: true, align: 'center' },
        { text: 'Hatch', value: 'Hatch', sortable: true, align: 'center' },
        { text: 'O', value: 'O', sortable: true, align: 'center' },
        { text: 'M', value: 'M', sortable: true, align: 'center' },
        { text: 'Y', value: 'Y', sortable: true, align: 'center' },
        { text: 'V', value: 'V', sortable: true, align: 'center' },
        { text: 'Comment', value: 'Comment', sortable: true, align: 'center' },
        { text: 'Actions', value: 'action', sortable: false, align: 'center' }
      ];
    },
    farmList() {
      let uniqueFarms = [...new Set(this.items.map(item => item.Farm))];
      this.sortArray(uniqueFarms);
      return uniqueFarms;
    },
    lineList() {
      let uniqueLines = [...new Set(this.items.map(item => item.Line))];
      this.sortNumArray(uniqueLines);
      return uniqueLines;
    },
    sexList() {
      let uniqueSexes = [...new Set(this.items.map(item => item.Sex))];
      return uniqueSexes;
    },
    startMgHList() {
      let uniqueItems = [...new Set(this.items.map(item => item.StartMG_H))];
      this.sortNumArray(uniqueItems);
      return uniqueItems;
    },
    tableHeight() {
      let tableSize = this.windowSize.y - 295;

      if (this.$vuetify.breakpoint.name == 'xs' || this.$vuetify.breakpoint.name === 'sm') {
        tableSize += 4; // v-app-bar shorter for xs and sm
      }
      if (tableSize < 200) tableSize = 200;
      return tableSize;
    },
 },
  methods: {
    addItem() {
      this.$refs.form.resetValidation();
      this.isAdd = true;
      this.editedItem = {
      };
      this.showChangeDialog = true;
    },
    confirmDeleteItem(item) {
      this.itemToDelete = item;
      this.showDeleteDialog = true;
    },
    closeChangeDialog() {
      this.showChangeDialog = false;
    },
    async deleteItem() {
      try {
        this.isBusy = true;
        this.showDeleteDialog = false;
        await iagApi.deleteDissect(this.itemToDelete.Id);
        await this.getDissectData();
      } catch (error) {
         this.$root.showNoticeError(error.message);
      }
      finally {
        this.isBusy = false;
      }
    },
    downloadCsv() {
      try {
        const items = this.items.map(x => {
          return {
            Id: x.Id,
            Farm: x.Farm,
            Line: x.Line,
            Sex: x.Sex,
            StartMG_H: x.StartMG_H,
            Hatch: x.Hatch,
            O: x.O,
            M: x.M,
            Y: x.Y,
            V: x.V,
            Comment: x.Comment ? x.Comment : ''
          }
        });
        var csv = json2csv.convert(items);
        const fileName = `Iag-Dissect.csv`;
        this.$root.downloadCsvLocal(fileName, csv);
      }
      catch (error) {
        this.$root.showNoticeError(error);
      }
    },
    editItem(item) {
      this.$refs.form.resetValidation();
      this.isAdd = false;
      this.editedItem = {...item}

      this.showChangeDialog = true;
    },
    async getDissectData() {
      try {
        this.isBusy = true;
        let response = await iagApi.getDissects();
        this.items = await response.json();
      } catch (error) {
         this.$root.showNoticeError(error.message);
      }
      finally {
        this.isBusy = false;
      }
    },
    onResize() {
      this.windowSize = { x: window.innerWidth, y: window.innerHeight }
    },
    async saveItem() {
      try {
        this.isBusy = true;

        //* Change focus away from v-combobox values and sleep briefly. This is
        //* to allow stupid v-combobox to set value if user typed a new value.
        this.$refs.saveAction.focus();
        await this.sleep(100);

        this.closeChangeDialog();

        let payload = {...this.editedItem};
        let response = await iagApi.postDissect(payload);
        console.log(response);

        await this.getDissectData();
      } catch (error) {
         this.$root.showNoticeError(error.message);
      }
      finally {
        this.isBusy = false;
      }
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    sortArray(arr) {
      let sortedArray = arr.sort();
      return sortedArray;
    },
    sortNumArray(arr) {
      arr.sort((x,y) => {
        let v = parseFloat(x) - parseFloat(y);
        return v;
      });
    },
    toAlphaUpperCase(value) {
      let testVal = (value || "");
      const regex = /[^A-Za-z]/g;
      testVal = testVal.replace(regex, "");
      return testVal.toUpperCase();
    }
  }
}
</script>
